/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { DealEvent } from '@/services/requests/housien-services/DealsService';

/**
 * One rendered timeline entry, already collapsed and attributed.
 *
 * `titleKey` is resolved against the `features/deals` namespace. `actor` is
 * the person to credit; system-written entries carry none and render the
 * AUTOMÁTICO badge instead. `autoMove` carries the stage transition a
 * trigger performed as a side effect of the entry's fact (e.g. recording a
 * proposal pulling the deal into the proposal stage).
 */
export interface TimelineEntry {
  titleKey: string;
  event: DealEvent;
  actor: string | null;
  /** The `from`/`to` pair of a stage move absorbed into this entry. */
  autoMove: { from: string; to: string } | null;
}

/** Event types the backend writes without a human behind them. */
const SYSTEM_EVENT_TYPES = new Set([
  'lease_draft_created',
  'sale_record_created',
  'lease_draft_creation_failed',
  'sale_record_creation_failed',
  'client_origin_unresolved',
  'property_status_sync_failed',
]);

const TITLE_KEY_BY_TYPE: Record<string, string> = {
  created: 'detail.timeline.created',
  contact_recorded: 'detail.timeline.contactRecorded',
  proposal_recorded: 'detail.timeline.proposalRecorded',
  proposal_countered: 'detail.timeline.proposalCountered',
  lease_draft_created: 'detail.timeline.leaseDraftCreated',
  sale_record_created: 'detail.timeline.saleRecordCreated',
  lease_draft_creation_failed: 'detail.timeline.leaseDraftFailed',
  sale_record_creation_failed: 'detail.timeline.saleRecordFailed',
  client_origin_unresolved: 'detail.timeline.clientOriginUnresolved',
  property_status_sync_failed: 'detail.timeline.propertySyncFailed',
};

/**
 * Title key for a stage move, specialized for the moves that mean more
 * than a step (pause, resume, win, loss).
 */
function stageChangeTitleKey(event: DealEvent): string {
  if (event.to === 'paused') return 'detail.timeline.paused';
  if (event.from === 'paused') return 'detail.timeline.resumed';
  if (event.to === 'won') return 'detail.timeline.won';
  if (event.to === 'lost') return 'detail.timeline.lost';
  return 'detail.timeline.stageChanged';
}

/**
 * Title key for any event type, falling back to the raw type name.
 */
function titleKeyOf(event: DealEvent): string {
  if (event.type === 'stage_changed') return stageChangeTitleKey(event);
  // `proposal_answered` is two different facts sharing one type. Left to
  // the map it had no entry at all and rendered its own wire name.
  if (event.type === 'proposal_answered') {
    return event.status === 'accepted'
      ? 'detail.timeline.proposalAccepted'
      : 'detail.timeline.proposalRefused';
  }
  return TITLE_KEY_BY_TYPE[event.type] ?? event.type;
}

/**
 * The colour of a timeline entry's dot, by what the entry *is*.
 *
 * The dots used to take the attendance's accent, so every human entry on
 * a screen was the same colour and the dot carried no information beyond
 * "a person did this". Reading the type instead lets the rail be scanned
 * for the offers: an offer made, one answered with another, one refused.
 *
 * @param event - The event behind the entry.
 * @returns A CSS colour token reference.
 */
export function timelineDotColor(event: DealEvent): string {
  return proposalDotColor(event) ?? STAGE_DOT_COLOR.get(event.to ?? '') ?? 'var(--border-medium)';
}

/** Dot colour of a terminal stage move, keyed by the stage it lands on. */
const STAGE_DOT_COLOR = new Map<string, string>([
  ['won', 'var(--status-success-text)'],
  ['lost', 'var(--status-error-text)'],
]);

/** The dot colour of a proposal event, or null when the event is not one. */
function proposalDotColor(event: DealEvent): string | null {
  if (event.type === 'proposal_recorded') return 'var(--seed-base)';
  if (event.type === 'proposal_countered') return 'var(--accent-mostarda)';
  if (event.type !== 'proposal_answered') return null;
  return event.status === 'accepted' ? 'var(--status-success-text)' : 'var(--status-error-text)';
}

/**
 * Whether two events happened close enough together (2s) to be one action.
 */
function sameMoment(a: DealEvent, b: DealEvent): boolean {
  const ta = new Date(a.timestamp).getTime();
  const tb = new Date(b.timestamp).getTime();
  if (Number.isNaN(ta) || Number.isNaN(tb)) return false;
  return Math.abs(ta - tb) <= 2_000;
}

/**
 * Whether *neighbor* is the plain, dated stage move that *proposal* pulled
 * along -- a real move, with both ends, written in the same instant.
 *
 * @param proposal - The proposal event being rendered.
 * @param neighbor - An adjacent event, or undefined past the list edge.
 * @returns True when the neighbor is that absorbed move.
 */
function isAbsorbedMove(proposal: DealEvent, neighbor: DealEvent | undefined): boolean {
  if (!neighbor || neighbor.type !== 'stage_changed') return false;
  if (!neighbor.from || !neighbor.to) return false;
  return sameMoment(proposal, neighbor);
}

/**
 * The plain stage move adjacent to a proposal event, when the backend
 * wrote both as one action — that move was triggered, not performed.
 */
function absorbedMoveFor(events: DealEvent[], index: number): DealEvent | null {
  const proposal = events[index];
  for (const neighbor of [events[index - 1], events[index + 1]]) {
    if (isAbsorbedMove(proposal, neighbor)) return neighbor;
  }
  return null;
}

/**
 * Maps raw deal events into rendered timeline entries, newest first.
 *
 * A stage move written in the same instant as a proposal is folded into
 * the proposal entry as its `autoMove` sub-line instead of appearing as a
 * second, seemingly human, entry.
 *
 * @param events - The deal's raw event list.
 * @returns The display entries, most recent first.
 */
export function toTimelineEntries(events: DealEvent[]): TimelineEntry[] {
  const absorbed = new Set<DealEvent>();
  const entries: TimelineEntry[] = [];

  events.forEach((event, index) => {
    if (absorbed.has(event)) return;
    let autoMove: TimelineEntry['autoMove'] = null;
    if (event.type === 'proposal_recorded') {
      const move = absorbedMoveFor(events, index);
      if (move) {
        absorbed.add(move);
        autoMove = { from: move.from as string, to: move.to as string };
      }
    }
    entries.push({
      titleKey: titleKeyOf(event),
      event,
      actor: SYSTEM_EVENT_TYPES.has(event.type) ? null : event.actor ?? null,
      autoMove,
    });
  });

  return entries.sort(
    (a, b) => new Date(b.event.timestamp).getTime() - new Date(a.event.timestamp).getTime(),
  );
}

/**
 * The most recent event that moved the deal into `paused`, if any.
 */
export function lastPauseEvent(events: DealEvent[]): DealEvent | null {
  const pauses = events.filter(
    (event) => event.type === 'stage_changed' && event.to === 'paused',
  );
  if (pauses.length === 0) return null;
  return pauses.reduce((latest, event) => (
    new Date(event.timestamp).getTime() > new Date(latest.timestamp).getTime() ? event : latest
  ));
}

/**
 * The id carried by the first matching handoff event, if any.
 */
export function handoffIdFrom(
  events: DealEvent[],
  type: 'lease_draft_created' | 'sale_record_created',
): string | null {
  const event = events.find((e) => e.type === type);
  if (!event) return null;
  return (type === 'lease_draft_created' ? event.lease_id : event.sale_id) ?? null;
}

/**
 * Why the record a win should have opened was not opened, if it wasn't.
 *
 * Opening the Sale or the Lease is best-effort on purpose -- a win is
 * never reverted over it -- so the failure lives only on the timeline.
 * That is the right place for it to be *recorded* and the wrong place
 * for it to be *found*: the screen showed a won attendance with no
 * handoff link and no explanation, and the commonest cause is a fact
 * the broker can fix in a click (a won attendance with no client).
 *
 * @param events - The attendance's timeline.
 * @returns The reason the backend gave, or null when nothing failed.
 */
export function handoffFailureFrom(events: DealEvent[]): string | null {
  const failure = events.find(
    (e) => e.type === 'lease_draft_creation_failed'
      || e.type === 'sale_record_creation_failed',
  );
  return failure ? ((failure as { reason?: string }).reason ?? '') : null;
}

/**
 * Timestamp of the most recent event, if any.
 */
export function lastEventTimestamp(events: DealEvent[]): string | undefined {
  if (events.length === 0) return undefined;
  return events.reduce((latest, event) => (
    new Date(event.timestamp).getTime() > new Date(latest.timestamp).getTime() ? event : latest
  )).timestamp;
}
